import { endOfWeek, format, getYear, startOfWeek, subWeeks } from "date-fns";

import type { CalendarColor } from "@ctrl/controller-rpc";

/** A calendar event carrying meeting notes, reduced to what the list needs. */
export type Meeting = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  /** Unset when the event's calendar is unknown (e.g. not loaded yet). */
  calendarColor?: CalendarColor;
};

export type MeetingGroup = {
  key: string;
  title: string;
  meetings: Meeting[];
};

/**
 * Group meetings the way the Upcoming task list groups weeks, newest first:
 * events after the current week ("Upcoming"), the current week ("This week"),
 * the previous week ("Last week"), then one group per month of the current
 * year, then one group per earlier year.
 *
 * Expects `meetings` sorted by start date, most recent first (the order of
 * `controller.event.withNotes()`): groups are emitted in first-appearance
 * order, so a sorted input yields groups sorted newest first.
 */
export function groupMeetings(
  meetings: Meeting[],
  now: Date,
  isWeekStartOnSunday: boolean
): MeetingGroup[] {
  const weekStartsOn = isWeekStartOnSunday ? 0 : 1;
  const thisWeekStart = startOfWeek(now, { weekStartsOn });
  const thisWeekEnd = endOfWeek(now, { weekStartsOn });
  const lastWeekStart = subWeeks(thisWeekStart, 1);
  const currentYear = getYear(now);

  const groupOf = (start: Date): { key: string; title: string } => {
    if (start > thisWeekEnd) return { key: "upcoming", title: "Upcoming" };
    if (start >= thisWeekStart) return { key: "this-week", title: "This week" };
    if (start >= lastWeekStart) return { key: "last-week", title: "Last week" };
    if (getYear(start) === currentYear) {
      return {
        key: `month-${start.getMonth()}`,
        title: format(start, "MMMM"),
      };
    }
    return {
      key: `year-${getYear(start)}`,
      title: format(start, "yyyy"),
    };
  };

  const groups = new Map<string, MeetingGroup>();
  for (const meeting of meetings) {
    const { key, title } = groupOf(meeting.start);
    const group = groups.get(key);
    if (group) {
      group.meetings.push(meeting);
    } else {
      groups.set(key, { key, title, meetings: [meeting] });
    }
  }
  return [...groups.values()];
}
